import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  VipDashboardResponse,
  VipQuestLogsQuery,
  VipQuestLogsResponse,
  VipStatsPeriod,
  VipStatsResponse,
} from './vip.models';

@Injectable({ providedIn: 'root' })
export class VipService {
  private readonly questLogCache = new Map<string, VipQuestLogsResponse>();
  private readonly questLogRequests = new Map<string, Observable<VipQuestLogsResponse>>();

  constructor(private readonly http: HttpClient) {}

  getStats(period: VipStatsPeriod = 'all'): Observable<VipStatsResponse> {
    const params = period !== 'all'
      ? new HttpParams().set('period', period)
      : undefined;

    return this.http.get<VipStatsResponse>(`${environment.GATEWAY_URL}/vip/stats`, {
      withCredentials: true,
      params,
    });
  }

  getDashboard(userId: number): Observable<VipDashboardResponse> {
    return this.http.get<VipDashboardResponse>(`${environment.GATEWAY_URL}/vip/dashboard/${userId}`, {
      withCredentials: true,
    });
  }

  getQuestLogs(userId: number, query: VipQuestLogsQuery): Observable<VipQuestLogsResponse> {
    const cacheKey = this.getQuestLogCacheKey(userId, query);
    const cachedResponse = this.questLogCache.get(cacheKey);

    if (cachedResponse) {
      return of(cachedResponse);
    }

    const inFlightRequest = this.questLogRequests.get(cacheKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    let params = new HttpParams()
      .set('gameSlug', query.gameSlug)
      .set('questNumber', String(query.questNumber))
      .set('page[size]', String(query.pageSize))
      .set('page[number]', String(query.page + 1));

    if (query.questDate) {
      params = params.set('questDate', query.questDate);
    }

    const request$ = this.http.get<VipQuestLogsResponse>(`${environment.GATEWAY_URL}/vip/quest-logs/${userId}`, {
      withCredentials: true,
      params,
    }).pipe(
      tap((response: VipQuestLogsResponse) => this.questLogCache.set(cacheKey, response)),
      finalize(() => this.questLogRequests.delete(cacheKey)),
      shareReplay(1),
    );

    this.questLogRequests.set(cacheKey, request$);

    return request$;
  }

  private getQuestLogCacheKey(userId: number, query: VipQuestLogsQuery): string {
    return [
      userId,
      query.gameSlug,
      query.questNumber,
      query.questDate || '',
      query.page,
    ].join('|');
  }
}
